import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Video, Square, Loader2, RotateCcw, Save, Trash2, PlayCircle, MicOff,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * VideoIntroRecorder — 60-second in-browser intro capture for the Talent
 * Passport. Uses MediaRecorder + getUserMedia (video+audio), enforces a
 * hard 60s cap, previews locally before upload, and hits
 * POST /api/talent/video-intro on save.
 *
 * States: idle → requesting → live → recording → stopped(preview) → uploading
 * → done. Errors surface as toasts and reset back to idle.
 */
const MAX_SECONDS = 60;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default 3-question prompt set — used when the talent hasn't customised
// via /talent/video-intro/prompts. Server exposes the same defaults; we
// keep them here as a fallback so the UI still works if the prompts fetch
// races the recorder mount. Windows sum to 60s.
const DEFAULT_PROMPTS = [
    { key: "who",         label: "Who you are",     detail: "Name, role, and one line of personality.",         window_seconds: 20 },
    { key: "build",       label: "What you build",  detail: "The kind of problems you love solving.",             window_seconds: 20 },
    { key: "superpower",  label: "Your superpower", detail: "One thing recruiters won't find on your resume.",   window_seconds: 20 },
];

// Turn a prompt list (each with window_seconds) into [start, end) tuples
// so the recorder overlay can auto-advance based on elapsed seconds.
const _promptWindows = (prompts) => {
    let cursor = 0;
    return prompts.map((p) => {
        const start = cursor;
        cursor += Math.max(5, Number(p.window_seconds) || 20);
        return { ...p, window: [start, Math.min(cursor, MAX_SECONDS)] };
    });
};

const _pickMime = () => {
    // Prefer webm/vp9 for size; fall back to what the browser actually
    // supports. iOS Safari 14+ speaks mp4/h264 only.
    const candidates = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
        "video/mp4",
    ];
    if (typeof MediaRecorder === "undefined") return "";
    return candidates.find((c) => MediaRecorder.isTypeSupported(c)) || "";
};

export const VideoIntroRecorder = () => {
    const [existing, setExisting] = useState(null);      // {intro_id, playback_token, ...}
    const [phase, setPhase] = useState("idle");          // idle | requesting | live | recording | stopped | uploading
    const [elapsed, setElapsed] = useState(0);           // seconds
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    // Prompts — fetched from /talent/video-intro/prompts on mount. Falls
    // back to DEFAULT_PROMPTS so the recorder still works if the API 404s.
    const [prompts, setPrompts] = useState(_promptWindows(DEFAULT_PROMPTS));

    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const videoRef = useRef(null);
    const previewVideoRef = useRef(null);
    const tickRef = useRef(null);

    // ---- Load existing intro (if any) --------------------------------------
    const loadExisting = useCallback(async () => {
        try {
            const { data } = await api.get("/talent/video-intro");
            setExisting(data.intro_id ? data : null);
        } catch (err) {
            // Non-fatal — talent just hasn't recorded yet.
            console.warn("[VideoIntroRecorder] loadExisting failed:", err?.message || err);
        }
    }, []);
    // ---- Load view analytics (how many employers watched this month) ------
    const [viewStats, setViewStats] = useState(null);
    const loadViewStats = useCallback(async () => {
        try {
            const { data } = await api.get("/talent/video-intro/analytics?days=30");
            setViewStats(data);
        } catch { /* silent — analytics is bonus context */ }
    }, []);
    // ---- Load custom prompts (if any) --------------------------------------
    const loadPrompts = useCallback(async () => {
        try {
            const { data } = await api.get("/talent/video-intro/prompts");
            if (Array.isArray(data?.prompts) && data.prompts.length === 3) {
                setPrompts(_promptWindows(data.prompts));
            }
        } catch (err) {
            // Silent fallback to defaults.
            console.warn("[VideoIntroRecorder] loadPrompts failed:", err?.message || err);
        }
    }, []);
    useEffect(() => { loadExisting(); loadPrompts(); loadViewStats(); }, [loadExisting, loadPrompts, loadViewStats]);

    // ---- Cleanup on unmount ------------------------------------------------
    const stopStream = useCallback(() => {
        try {
            if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
        } catch { /* noop */ }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, []);
    useEffect(() => () => {
        stopStream();
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [stopStream, previewUrl]);

    // Attach the MediaStream once the <video> element is in the DOM.
    // startCamera() flips phase → "live" but the ref is null during that
    // render. This effect runs AFTER commit, so videoRef.current exists
    // by the time we try to bind srcObject + play().
    useEffect(() => {
        if ((phase === "live" || phase === "recording") && streamRef.current && videoRef.current) {
            if (videoRef.current.srcObject !== streamRef.current) {
                videoRef.current.srcObject = streamRef.current;
                videoRef.current.play().catch(() => { /* autoplay refusal is fine — user can tap */ });
            }
        }
    }, [phase]);

    // ---- Start camera + mic ------------------------------------------------
    const startCamera = async () => {
        if (!navigator.mediaDevices?.getUserMedia) {
            toast.error("Your browser doesn't support in-app recording. Try Chrome, Firefox, or Safari 14+.");
            return;
        }
        setPhase("requesting");
        // Fire-and-forget funnel event — analytics tile counts this against
        // eventually-recorded to measure drop-off.
        api.post("/talent/video-intro/event", { event: "started" }).catch(() => { /* silent */ });
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
                audio: true,
            });
            streamRef.current = stream;
            // Flip to "live" FIRST so the <video> element renders — the
            // ref-attach effect below wires up srcObject once the DOM
            // node exists. Assigning here would silently no-op because
            // videoRef.current is still null until React commits.
            setPhase("live");
        } catch (err) {
            console.error(err);
            const msg = err?.name === "NotAllowedError"
                ? "Camera and mic access denied — enable them in your browser settings and try again."
                : err?.name === "NotFoundError"
                ? "No camera or microphone found on this device."
                : "Couldn't start your camera. Try reloading the page.";
            toast.error(msg);
            setPhase("idle");
        }
    };

    // ---- Start recording ---------------------------------------------------
    const startRecording = () => {
        if (!streamRef.current) return;
        chunksRef.current = [];
        const mime = _pickMime();
        try {
            const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
            rec.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            rec.onstop = () => {
                const type = chunksRef.current[0]?.type || mime || "video/webm";
                const blob = new Blob(chunksRef.current, { type });
                setRecordedBlob(blob);
                const url = URL.createObjectURL(blob);
                setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
                setPhase("stopped");
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t) => t.stop());
                    streamRef.current = null;
                }
                if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
            };
            recorderRef.current = rec;
            rec.start(500);  // gather chunks every 500ms so a bad tab-close still keeps data
            setPhase("recording");
            setElapsed(0);
            tickRef.current = setInterval(() => {
                setElapsed((e) => {
                    const next = e + 1;
                    if (next >= MAX_SECONDS) {
                        stopRecording();
                        return MAX_SECONDS;
                    }
                    return next;
                });
            }, 1000);
        } catch (err) {
            console.error(err);
            toast.error("Recording failed to start. Try a different browser or lower resolution.");
            setPhase("live");
        }
    };

    const stopRecording = () => {
        try {
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                recorderRef.current.stop();
            }
        } catch { /* noop */ }
    };

    // Iter 90 — Retake analytics. Per-mount counter so admins can chart
    // avg retakes per submitted intro to spot friction (too-long records,
    // teleprompter missing, camera issues). Reset when the component
    // unmounts, so we only count retakes for THIS attempt session.
    const retakeCountRef = useRef(0);

    const discardTake = () => {
        // Only count as a retake if there was actually a preview to discard.
        // The initial "idle → live" flip also passes through here on some
        // paths, and we don't want to inflate the counter.
        if (previewUrl || recordedBlob) {
            retakeCountRef.current += 1;
            api.post("/talent/video-intro/event", {
                event: "retake",
                meta: { retake_index: retakeCountRef.current,
                         elapsed_seconds: elapsed },
            }).catch(() => { /* silent — analytics is fire-and-forget */ });
        }
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setRecordedBlob(null);
        setElapsed(0);
        setPhase("idle");
    };

    // ---- Save (upload) -----------------------------------------------------
    const saveIntro = async () => {
        if (!recordedBlob) return;
        setPhase("uploading");
        try {
            const form = new FormData();
            const ext = (recordedBlob.type || "video/webm").split("/")[1].split(";")[0] || "webm";
            form.append("file", recordedBlob, `intro.${ext}`);
            const { data } = await api.post("/talent/video-intro", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // Iter 90 — stamp the total retake count on the row via a
            // dedicated event so the analytics query can compute
            // "avg retakes per submitted intro". Fired AFTER the upload
            // succeeded so a failed save doesn't skew the metric.
            api.post("/talent/video-intro/event", {
                event: "submitted",
                meta: { retakes_before_submit: retakeCountRef.current,
                         final_duration_seconds: elapsed,
                         intro_id: data?.intro_id },
            }).catch(() => { /* silent */ });
            toast.success("Intro saved — recruiters can now see the real you.");
            discardTake();
            retakeCountRef.current = 0;  // reset for next session
            setExisting(data);
        } catch (err) {
            toast.error(formatApiError(err));
            setPhase("stopped");
        }
    };

    const deleteExisting = async () => {
        if (!window.confirm("Delete your video intro? Employers won't be able to view it anymore.")) return;
        try {
            await api.delete("/talent/video-intro");
            toast.success("Video intro deleted.");
            setExisting(null);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    // ---- Playback URL for the existing intro -------------------------------
    // The playback endpoint is auth-scoped by a short-lived HMAC token so
    // <video src> can play without an Authorization header. Token TTL is 2h,
    // refreshed by loadExisting() on mount.
    const playbackSrc = existing?.playback_token
        ? `${BACKEND_URL}/api/talent/video-intro/file/${existing.playback_token}`
        : null;

    // ---- Render ------------------------------------------------------------
    return (
        <div data-testid="video-intro-recorder" className="space-y-4">
            {/* Existing intro playback */}
            {existing && phase === "idle" && (
                <div data-testid="video-intro-existing" className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                        <div>
                            <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1.5">
                                <PlayCircle size={12} /> Your video intro is live
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                                Recorded {existing.created_at ? new Date(existing.created_at).toLocaleDateString() : "recently"} · Employers see this on your card.
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setExisting(null)}
                                data-testid="video-intro-rerecord"
                                className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                            >
                                <RotateCcw size={12} /> Re-record
                            </button>
                            <button
                                onClick={deleteExisting}
                                data-testid="video-intro-delete-existing"
                                className="text-xs px-3 py-1.5 rounded border border-rose-500/40 text-rose-600 hover:bg-rose-500/10 inline-flex items-center gap-1.5"
                            >
                                <Trash2 size={12} /> Delete
                            </button>
                        </div>
                    </div>
                    {playbackSrc && (
                        <video
                            src={playbackSrc}
                            controls
                            playsInline
                            className="w-full max-w-md rounded-lg bg-black"
                            data-testid="video-intro-playback"
                        />
                    )}
                    {/* Views stat — makes the recorded intro feel high-ROI
                        so talent has a reason to iterate and re-record. */}
                    {viewStats && viewStats.has_intro && (
                        <div className="mt-3 flex items-center gap-4 flex-wrap text-xs" data-testid="video-intro-views-stat">
                            <div className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                                <PlayCircle size={12} />
                                <span className="font-mono font-semibold tabular-nums">{viewStats.views_this_month}</span>
                                <span className="text-muted-foreground">
                                    employer{viewStats.views_this_month === 1 ? "" : "s"} viewed this month
                                </span>
                            </div>
                            {viewStats.total_views > viewStats.views_this_month && (
                                <div className="text-muted-foreground">
                                    · <span className="font-mono tabular-nums">{viewStats.total_views}</span> lifetime
                                </div>
                            )}
                            {viewStats.last_viewed_at && (
                                <div className="text-muted-foreground">
                                    · last viewed {new Date(viewStats.last_viewed_at).toLocaleDateString()}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Idle — first-time recorder or after Re-record was clicked */}
            {(!existing || (existing && phase !== "idle" && !recordedBlob)) && phase === "idle" && (
                <div data-testid="video-intro-idle" className="rounded-xl border border-dashed border-border p-6 text-center">
                    <div className="w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto">
                        <Video size={20} />
                    </div>
                    <div className="mt-3 font-heading font-semibold text-sm text-foreground">Record your 60-second intro</div>
                    <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto">
                        Show recruiters the real you — who you are, what you love to build, and one thing they won&apos;t find on your resume.
                    </p>
                    {/* 3-question prompt preview — sets expectations so
                        first-timers don't freeze on the red light. */}
                    <div className="mt-4 mx-auto max-w-md text-left space-y-2" data-testid="video-intro-prompts">
                        {prompts.map((p, i) => (
                            <div key={p.key || i} className="flex items-start gap-2 text-xs" data-testid={`video-intro-prompt-preview-${p.key || i}`}>
                                <span className="w-5 h-5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold flex items-center justify-center shrink-0 mt-0.5">
                                    {i + 1}
                                </span>
                                <div>
                                    <span className="font-semibold text-foreground">{p.label}</span>{" "}
                                    <span className="text-muted-foreground">— {p.detail}</span>{" "}
                                    <span className="text-slate-500 font-mono text-[10px]">(~{p.window[1] - p.window[0]}s)</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                        <button
                            onClick={startCamera}
                            data-testid="video-intro-start"
                            className="btn-green text-sm inline-flex items-center gap-1.5"
                        >
                            <Video size={13} /> Start camera
                        </button>
                        <button
                            onClick={() => {
                                api.post("/talent/video-intro/event", { event: "skipped" })
                                    .catch(() => { /* silent — analytics best-effort */ });
                                toast("You can record your intro anytime from your Passport.",
                                    { duration: 5000 });
                            }}
                            data-testid="video-intro-skip"
                            className="text-xs px-3 py-2 text-muted-foreground hover:text-foreground underline underline-offset-2"
                        >
                            I&apos;ll decide later
                        </button>
                    </div>
                </div>
            )}

            {/* Requesting permission */}
            {phase === "requesting" && (
                <div className="rounded-xl border border-border p-6 text-center text-sm text-muted-foreground" data-testid="video-intro-requesting">
                    <Loader2 size={16} className="animate-spin inline mr-2" />
                    Waiting for camera + mic permissions…
                </div>
            )}

            {/* Live preview + record button */}
            {(phase === "live" || phase === "recording") && (
                <div className="space-y-3" data-testid="video-intro-live">
                    <div className="relative">
                        <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            className="w-full max-w-md rounded-lg bg-black"
                        />
                        {phase === "recording" && (
                            <div
                                data-testid="video-intro-timer"
                                className="absolute top-2 right-2 bg-rose-600 text-white text-xs font-mono font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1.5"
                            >
                                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                                {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
                                {String(elapsed % 60).padStart(2, "0")} / 01:00
                            </div>
                        )}
                        {/* Live prompt overlay — picks the prompt whose
                            [start, end) window contains ``elapsed``. In the
                            live-but-not-yet-recording state we show the first
                            prompt so the talent has a moment to read before
                            hitting record. */}
                        {(() => {
                            const activeIdx = phase === "recording"
                                ? prompts.findIndex(p => elapsed >= p.window[0] && elapsed < p.window[1])
                                : 0;
                            const idx = activeIdx >= 0 ? activeIdx : prompts.length - 1;
                            const active = prompts[idx];
                            return (
                                <div
                                    data-testid="video-intro-active-prompt"
                                    className="absolute bottom-2 left-2 right-2 bg-black/70 backdrop-blur text-white rounded-lg px-3 py-2 flex items-center gap-2 text-xs"
                                >
                                    <div className="flex gap-1 shrink-0">
                                        {prompts.map((_, i) => (
                                            <span
                                                key={i}
                                                data-testid={`video-intro-dot-${i}`}
                                                className={`w-1.5 h-1.5 rounded-full ${
                                                    i === idx ? "bg-emerald-400"
                                                    : i < idx ? "bg-white/60"
                                                    : "bg-white/25"
                                                }`}
                                            />
                                        ))}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold" data-testid={`video-intro-prompt-label-${active.key || idx}`}>
                                            {idx + 1}. {active.label}
                                        </div>
                                        <div className="text-white/70 text-[11px] truncate">{active.detail}</div>
                                    </div>
                                    <span className="font-mono text-[10px] text-white/60 shrink-0">
                                        {active.window[0]}–{active.window[1]}s
                                    </span>
                                </div>
                            );
                        })()}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {phase === "live" && (
                            <button
                                onClick={startRecording}
                                data-testid="video-intro-record"
                                className="text-sm px-4 py-2 rounded bg-rose-600 hover:bg-rose-700 text-white inline-flex items-center gap-1.5"
                            >
                                <span className="w-2.5 h-2.5 rounded-full bg-white" /> Start recording
                            </button>
                        )}
                        {phase === "recording" && (
                            <button
                                onClick={stopRecording}
                                data-testid="video-intro-stop"
                                className="text-sm px-4 py-2 rounded bg-slate-800 hover:bg-slate-900 text-white inline-flex items-center gap-1.5"
                            >
                                <Square size={12} /> Stop
                            </button>
                        )}
                        <button
                            onClick={() => { stopStream(); setPhase("idle"); }}
                            className="btn-outline text-sm px-3 py-2 inline-flex items-center gap-1.5"
                        >
                            <MicOff size={12} /> Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Preview + save */}
            {phase === "stopped" && previewUrl && (
                <div className="space-y-3" data-testid="video-intro-preview">
                    <video
                        ref={previewVideoRef}
                        src={previewUrl}
                        controls
                        playsInline
                        className="w-full max-w-md rounded-lg bg-black"
                    />
                    <div className="text-xs text-muted-foreground">
                        Preview your take. {elapsed}s · {(recordedBlob?.size / 1_000_000).toFixed(1)}MB — save it or record another.
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={saveIntro}
                            data-testid="video-intro-save"
                            className="btn-green text-sm inline-flex items-center gap-1.5"
                        >
                            <Save size={13} /> Save intro
                        </button>
                        <button
                            onClick={discardTake}
                            data-testid="video-intro-retake"
                            className="btn-outline text-sm px-3 py-2 inline-flex items-center gap-1.5"
                        >
                            <RotateCcw size={12} /> Record another take
                        </button>
                    </div>
                </div>
            )}

            {/* Uploading */}
            {phase === "uploading" && (
                <div className="rounded-xl border border-border p-6 text-center text-sm text-muted-foreground" data-testid="video-intro-uploading">
                    <Loader2 size={16} className="animate-spin inline mr-2" />
                    Uploading your intro… this can take a few seconds on slow connections.
                </div>
            )}
        </div>
    );
};

export default VideoIntroRecorder;
